import { toast } from 'react-toastify';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { Dialog } from '@mui/material';

import { apiClient } from '@api';
import { useAuthStore } from '@core/features/auth';
import i18n from '@i18n/i18n';

type SearchParams = {
  venue?: string;
};

/**
 * Magic link landing page. Exchanges the email auth token from the URL for a
 * JWT pair and redirects to the venue dashboard (or home).
 *
 * The exchange happens in `beforeLoad` (not in a component effect) so it runs
 * exactly once per navigation: React StrictMode double-invokes effects in dev
 * and discards the first mutation observer, which silently swallows mutation
 * callbacks — a route loader is immune to that. The token is single-use, so
 * firing exactly one request matters. On failure the user is sent to the
 * login page with an explanatory toast so they can request a new link.
 */
export const Route = createFileRoute('/_auth/auth/token/$email/$token')({
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return {
      venue: search.venue as string | undefined,
    };
  },
  beforeLoad: async ({ params: { email, token }, search }) => {
    const venueSlug = (search as SearchParams).venue;

    let tokens: { access: string; refresh: string } | null = null;
    try {
      const response = await apiClient.authObtainJwtFromEmailTokenCreate<true>(
        {
          body: { email, auth_token: token },
          throwOnError: true,
        }
      );
      tokens = response.data;
    } catch {
      tokens = null;
    }

    if (tokens) {
      useAuthStore.getState().setTokens({
        accessToken: tokens.access,
        refreshToken: tokens.refresh,
      });

      if (venueSlug) {
        throw redirect({ to: '/$venueSlug', params: { venueSlug } });
      }
      throw redirect({ to: '/' });
    }

    toast.error(i18n.t('authTokenInvalidOrExpired'));
    if (venueSlug) {
      throw redirect({ to: '/auth/$venueSlug', params: { venueSlug } });
    }
    throw redirect({ to: '/' });
  },
  // Only visible for the brief moment while the exchange request is pending.
  component: () => <Dialog open={true} scroll={'body'} fullWidth={true} />,
});
